import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';
import { toast } from 'sonner';

// WatchButton — Jira-style "Watch / Unwatch" toggle + count + popover with watcher list.
// Place in the TaskSheet header / TaskDetailPage toolbar.
export default function WatchButton({ taskId, compact = false }) {
  const { getOrgHeaders, API, user } = useAuth();
  const [watchers, setWatchers] = useState([]);
  const [isWatching, setIsWatching] = useState(false);
  const [busy, setBusy] = useState(false);

  const headers = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);

  const fetchWatchers = useCallback(async () => {
    if (!taskId) return;
    try {
      const { data } = await axios.get(`${API}/tasks/${taskId}/watchers`, headers());
      setWatchers(Array.isArray(data?.watchers) ? data.watchers : []);
      setIsWatching(!!data?.is_watching);
    } catch (err) { /* noop */ }
  }, [taskId, API, headers]);

  useEffect(() => { fetchWatchers(); }, [fetchWatchers]);

  const toggle = async () => {
    if (!taskId) return;
    setBusy(true);
    const prevWatching = isWatching;
    // Optimistic
    setIsWatching(!prevWatching);
    setWatchers(prev => prevWatching
      ? prev.filter(w => w.user_id !== user?.user_id)
      : (prev.some(w => w.user_id === user?.user_id) ? prev : [...prev, { user_id: user.user_id, name: user.name, email: user.email, avatar: user.avatar }])
    );
    try {
      if (prevWatching) {
        await axios.delete(`${API}/tasks/${taskId}/watchers`, headers());
        toast.success('Stopped watching');
      } else {
        await axios.post(`${API}/tasks/${taskId}/watchers`, {}, headers());
        toast.success('You are now watching this task');
      }
      fetchWatchers();
    } catch (err) {
      // Rollback
      setIsWatching(prevWatching);
      fetchWatchers();
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex items-center" data-testid="watch-button-wrapper">
      <Button
        size="sm"
        variant={isWatching ? 'default' : 'outline'}
        onClick={toggle}
        disabled={busy}
        className={`h-7 text-[11px] gap-1 ${
          isWatching ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'text-zinc-700 hover:text-blue-700 hover:border-blue-300'
        }`}
        data-testid="watch-toggle-button"
      >
        {isWatching ? <Eye size={12} weight="fill" /> : <EyeSlash size={12} />}
        {compact ? '' : (isWatching ? 'Watching' : 'Watch')}
        {watchers.length > 0 && <span className={`ml-0.5 ${isWatching ? 'text-blue-100' : 'text-zinc-400'}`}>·&nbsp;{watchers.length}</span>}
      </Button>
      {watchers.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button className="ml-1 flex -space-x-1.5" data-testid="watchers-list-trigger">
              {watchers.slice(0, 3).map(w => (
                <Avatar key={w.user_id} className="h-6 w-6 border-2 border-white">
                  <AvatarImage src={w.avatar} />
                  <AvatarFallback className={`${getAvatarStyle(w.user_id || w.name).bg} text-white text-[9px] font-bold`}>
                    {getInitials(w.name)}
                  </AvatarFallback>
                </Avatar>
              ))}
              {watchers.length > 3 && (
                <span className="h-6 px-1.5 rounded-full bg-zinc-100 text-zinc-600 text-[10px] font-bold inline-flex items-center border-2 border-white">
                  +{watchers.length - 3}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 p-2" data-testid="watchers-popover">
            <div className="text-[10px] uppercase tracking-wide font-bold text-zinc-500 mb-2 px-1">
              Watching ({watchers.length})
            </div>
            <div className="space-y-1 max-h-60 overflow-auto">
              {watchers.map(w => (
                <div key={w.user_id} className="flex items-center gap-2 px-1 py-1 rounded">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={w.avatar} />
                    <AvatarFallback className={`${getAvatarStyle(w.user_id || w.name).bg} text-white text-[9px] font-bold`}>
                      {getInitials(w.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="text-[12px] text-zinc-800 truncate">{w.name}</div>
                    <div className="text-[10px] text-zinc-400 truncate">{w.email}</div>
                  </div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
